import { PurchaseOrderTransition } from '@synergy/models';

import { ConverterCustomer } from '../models/internal/converter-customer';
import { ConverterJob } from '../models/internal/converterJob';
import { Demand } from '../../demands/models/demand';
import { DemandTransition } from '../models/demand/demandTransition';
import { DemandTransitionAccountItem } from '../models/demand/demandTransition-item';
import { Inquiry } from '../../inquiries/models/inquiry';
import { OfferTransition } from '../models/offer/offerTransition';
import { PurchaseOrder } from '../../purchase-orders/models/purchaseOrder';
import { SalesOrder } from '../../sales-orders/models/salesOrder';
import { SalesOrderTransition } from '../models/sales-order/salesOrdersTransition';
import { Transition } from '../models/type-transition';
import { TransitionTarget } from '../models/internal/transition-target';

export class TransitionFactory {
    public static createTransitions(converterJob: ConverterJob): Transition[] {
        if (converterJob.source instanceof Inquiry) {
            switch (converterJob.selectedTarget) {
                case TransitionTarget.PurchaseOrder: {
                    return this.createOfferTransition(converterJob);
                }
                default:
                    break;
            }
        }

        if (converterJob.source instanceof SalesOrder) {
            return this.createSalesOrderTransition(converterJob);
        }

        if (converterJob.source instanceof Demand) {
            return this.createDemandTransition(converterJob, converterJob.selectedCustomerId);
        }

        if (converterJob.source instanceof PurchaseOrder) {
            return this.createPurchaseOrderTransition(converterJob);
        }

        return [];
    }

    private static getCustomers(converterJob: ConverterJob) {
        return converterJob.selectedCustomerId >= 0
            ? converterJob.customers.filter(customer => customer.id === converterJob.selectedCustomerId)
            : converterJob.customers;
    }

    private static getSuppliers(customer: ConverterCustomer) {
        return customer.customerSuppliers.filter(supplier => supplier.items.some(item => item.isSelected));
    }

    private static createOfferTransition(converterJob: ConverterJob): OfferTransition[] {
        const transitions: OfferTransition[] = [];

        const customers = this.getCustomers(converterJob);
        customers.forEach(customer => {
            const selectedSuppliers = this.getSuppliers(customer);

            const transition: OfferTransition = {
                accountPurchaseId: customer.id,
                offers: selectedSuppliers.map(supplier => ({
                    id: supplier.id,
                    deliveryPrice: supplier.deliveryPrice ?? 0,
                    surcharge: supplier.surcharge ?? 0,
                    offerItems: supplier.items
                        .filter(item => item.isSelected)
                        .map(offerItem => ({
                            id: offerItem.id,
                            quantity: offerItem.quantity,
                            price: offerItem.price,
                        })),
                })),
            };
            if (transition.offers.length > 0 && transition.offers.every(offer => offer.offerItems.length > 0)) {
                transitions.push(transition);
            }
        });
        return transitions;
    }

    private static createSalesOrderTransition(converterJob: ConverterJob): SalesOrderTransition[] {
        const transition: SalesOrderTransition = {
            salesOrderId: converterJob.source.id,
            positions: [],
        };

        const customers = this.getCustomers(converterJob);
        customers.forEach(customer => {
            const suppliers = this.getSuppliers(customer);
            if (suppliers.length > 0 && transition.positions) {
                const supplier = customer.customerSuppliers[0];
                transition.positions.push({
                    id: customer.id,
                    deliveryPrice: supplier.deliveryPrice || 0,
                    surcharge: supplier.surcharge || 0,
                    items: supplier.items.filter(item => item.isSelected),
                });
            }
        });

        return [transition];
    }

    private static createDemandTransition(converterJob: ConverterJob, selectedCustomerId: number): DemandTransition[] {
        if (!converterJob.deliveryDate || !converterJob.deliveryDateType) {
            return [];
        }

        const customers =
            selectedCustomerId >= 0
                ? converterJob.customers.filter(customer => customer.id === selectedCustomerId)
                : converterJob.customers;
        const source = converterJob.source as Demand;

        const transition: DemandTransition = {
            demandId: source.id,
            demandAccounts: [],
            deliveryDate: converterJob.deliveryDate,
            deliveryDateType: converterJob.deliveryDateType,
        };

        customers.forEach(converterCustomer => {
            const selectedSuppliers = converterCustomer.customerSuppliers.filter(supplier =>
                supplier.items.some(item => item.isSelected)
            );

            const demandAccount = source.demandAccounts.find(
                account =>
                    account.demandAccountItems.some(item => item.deliveryAddress.id === converterCustomer.id) &&
                    account.customerContact.id === converterCustomer.customerContact?.id
            );

            if (demandAccount && selectedSuppliers.length > 0) {
                selectedSuppliers.forEach(supplier => {
                    const demandAccountItems = supplier.items
                        .filter(item => item.isSelected)
                        .map<DemandTransitionAccountItem>(item => ({
                            id: item.id,
                            quantity: item.quantity,
                            price: item.price,
                        }));

                    const existingDemandAccount = transition.demandAccounts.find(
                        account => account.id === demandAccount.id
                    );
                    if (existingDemandAccount) {
                        existingDemandAccount.demandAccountItems =
                            existingDemandAccount.demandAccountItems.concat(demandAccountItems);
                    } else {
                        transition.demandAccounts.push({
                            id: demandAccount.id || 0,
                            demandAccountItems,
                            deliveryPrice: supplier.deliveryPrice || 0,
                            surcharge: supplier.surcharge || 0,
                        });
                    }
                });
            }
        });

        return [transition];
    }

    private static createPurchaseOrderTransition(converterJob: ConverterJob): PurchaseOrderTransition[] {
        const transitions: PurchaseOrderTransition[] = [];

        const customers = this.getCustomers(converterJob);
        customers.forEach(customer => {
            const suppliers = this.getSuppliers(customer);
            suppliers.forEach(supplier => {
                const transition: PurchaseOrderTransition = {
                    purchaseOrderId: converterJob.source.id,
                    purchaseOrderItems: [],
                    deliveryPrice: supplier.deliveryPrice || 0,
                    surcharge: supplier.surcharge || 0,
                };

                supplier.items
                    .filter(item => item.isSelected)
                    .forEach(item =>
                        transition.purchaseOrderItems.push({
                            id: item.id,
                            price: item.price || 0,
                            quantity: item.quantity,
                            discount: item.discount || 0,
                            // Empty stays empty: a shop line has no rate, and a 0
                            // here read as a tax-free line (#2326 R-246).
                            tax: item.tax ?? null,
                            type: item.converterInfo as string,
                            position: item.position,
                        })
                    );
                if (transition.purchaseOrderItems.length > 0) {
                    transitions.push(transition);
                }
            });
        });

        return transitions;
    }
}
